import type { DamageZone } from "../types"
import type { Corner } from "./types"

// Top-down car for the consumer walk (zone picker + "where to stand" map).
// Front points UP; the driver's side is on the LEFT (US).
//
// Drawn for fingers, not for blueprints: the side panels are wide bands
// (44 units ≈ 46px on a phone) instead of true-to-scale slivers, so every
// panel is an easy tap. Zones are plain rectangles clipped by the body
// outline, which gives the rounded silhouette for free and keeps hit-testing
// identical to what's drawn.

export const CAR = {
  left: 32,
  right: 208,
  top: 18,
  bottom: 422,
  // Inner edges of the side bands.
  bandLeft: 76,
  bandRight: 164,
} as const

export const BODY_PATH =
  "M 66 20 C 92 13 148 13 174 20 C 197 27 208 44 208 70 L 208 370 C 208 396 197 412 174 419 C 148 426 92 426 66 419 C 43 412 32 396 32 370 L 32 70 C 32 44 43 27 66 20 Z"

// Greenhouse (windshield + roof + rear glass) for the drawing only.
export const GLASS_PATH =
  "M 84 148 C 100 140 140 140 156 148 L 158 290 C 142 298 98 298 82 290 Z"

// Wheels peek out under the body from above.
export const WHEELS: ReadonlyArray<{ x: number; y: number; w: number; h: number }> = [
  { x: 25, y: 76, w: 12, h: 50 },
  { x: 203, y: 76, w: 12, h: 50 },
  { x: 25, y: 316, w: 12, h: 50 },
  { x: 203, y: 316, w: 12, h: 50 },
]

export const MIRRORS: ReadonlyArray<string> = [
  "M 33 150 L 18 146 Q 14 152 18 158 L 33 160 Z",
  "M 207 150 L 222 146 Q 226 152 222 158 L 207 160 Z",
]

interface Rect {
  x: number
  y: number
  w: number
  h: number
}

const L = CAR.left
const R = CAR.right
const BL = CAR.bandLeft
const BR = CAR.bandRight
const SIDE_W = BL - L // == R - BR

// y-bands along the car, front → back.
// Bumpers and the windshield are ~42 units deep (+ finger slop on the outer
// edge for the bumpers) so they're ≥44px on a phone like every other panel.
const Y = {
  bumperFront: [CAR.top, 50],
  front: [50, 140],
  windshield: [140, 182],
  frontDoor: [140, 222],
  rearDoor: [222, 300],
  roof: [182, 300],
  rear: [300, 390],
  bumperRear: [390, CAR.bottom],
} as const

const span = ([a, b]: readonly [number, number]) => ({ y: a, h: b - a })

export const ZONE_RECT: Record<DamageZone, Rect> = {
  "front-bumper": { x: L, w: R - L, ...span(Y.bumperFront) },
  hood: { x: BL, w: BR - BL, ...span(Y.front) },
  "front-fender-left": { x: L, w: SIDE_W, ...span(Y.front) },
  "front-fender-right": { x: BR, w: SIDE_W, ...span(Y.front) },
  windshield: { x: BL, w: BR - BL, ...span(Y.windshield) },
  roof: { x: BL, w: BR - BL, ...span(Y.roof) },
  "front-door-left": { x: L, w: SIDE_W, ...span(Y.frontDoor) },
  "front-door-right": { x: BR, w: SIDE_W, ...span(Y.frontDoor) },
  "rear-door-left": { x: L, w: SIDE_W, ...span(Y.rearDoor) },
  "rear-door-right": { x: BR, w: SIDE_W, ...span(Y.rearDoor) },
  "quarter-panel-left": { x: L, w: SIDE_W, ...span(Y.rear) },
  "quarter-panel-right": { x: BR, w: SIDE_W, ...span(Y.rear) },
  trunk: { x: BL, w: BR - BL, ...span(Y.rear) },
  "rear-bumper": { x: L, w: R - L, ...span(Y.bumperRear) },
}

export function zoneCenter(zone: DamageZone): { x: number; y: number } {
  const r = ZONE_RECT[zone]
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 }
}

// Panel seams (hairlines) — drawn, not interactive.
export const SEAMS: ReadonlyArray<[number, number, number, number]> = [
  [L, Y.bumperFront[1], R, Y.bumperFront[1]],
  [L, Y.bumperRear[0], R, Y.bumperRear[0]],
  [BL, Y.front[0], BL, Y.rear[1]],
  [BR, Y.front[0], BR, Y.rear[1]],
  [L, Y.frontDoor[0], BL, Y.frontDoor[0]],
  [BR, Y.frontDoor[0], R, Y.frontDoor[0]],
  [L, Y.rearDoor[0], BL, Y.rearDoor[0]],
  [BR, Y.rearDoor[0], R, Y.rearDoor[0]],
  [L, Y.rear[0], BL, Y.rear[0]],
  [BR, Y.rear[0], R, Y.rear[0]],
]

/* ── Where to stand ─────────────────────────────────────────────────────── */

export interface StandPoint {
  x: number
  y: number
  // Where the person is looking (the car part being photographed).
  lookX: number
  lookY: number
}

const MID_X = (L + R) / 2
const MID_Y = (CAR.top + CAR.bottom) / 2

// Whole-car shots: a few big steps off each corner, looking at the car's
// middle. (The stand map pads its viewBox so these fit.)
export const CORNER_STAND: Record<Corner, StandPoint> = {
  FL: { x: -26, y: -40, lookX: MID_X, lookY: MID_Y },
  FR: { x: 266, y: -40, lookX: MID_X, lookY: MID_Y },
  RR: { x: 266, y: 480, lookX: MID_X, lookY: MID_Y },
  RL: { x: -26, y: 480, lookX: MID_X, lookY: MID_Y },
}

// Outward direction of a panel: which way you step back from it.
function outward(zone: DamageZone): { dx: number; dy: number } {
  if (zone.endsWith("-left")) return { dx: -1, dy: 0 }
  if (zone.endsWith("-right")) return { dx: 1, dy: 0 }
  if (zone === "front-bumper" || zone === "hood" || zone === "windshield") return { dx: 0, dy: -1 }
  if (zone === "rear-bumper" || zone === "trunk") return { dx: 0, dy: 1 }
  return { dx: -1, dy: 0 } // roof: from the driver's side
}

// Close to a panel: an arm's length off its outer edge, looking at it.
export function panelStand(zone: DamageZone, distance: number): StandPoint {
  const c = zoneCenter(zone)
  const o = outward(zone)
  const edgeX = o.dx < 0 ? L : o.dx > 0 ? R : c.x
  const edgeY = o.dy < 0 ? CAR.top : o.dy > 0 ? CAR.bottom : c.y
  return { x: edgeX + o.dx * distance, y: edgeY + o.dy * distance, lookX: c.x, lookY: c.y }
}

// A closer shot frames every damaged panel at that corner: stand off the
// average of their stand points.
export function closerStand(zones: readonly DamageZone[], corner: Corner): StandPoint {
  if (zones.length === 0) return CORNER_STAND[corner]
  const points = zones.map((z) => panelStand(z, 44))
  const avg = (k: keyof StandPoint) => points.reduce((s, p) => s + p[k], 0) / points.length
  return { x: avg("x"), y: avg("y"), lookX: avg("lookX"), lookY: avg("lookY") }
}
